import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ReferenceLine, ResponsiveContainer, CartesianGrid,
} from "recharts";
import type { Position } from "./types";

interface Props {
  positions: Position[];
  hoveredIndex: number | null;
  onHover: (index: number | null) => void;
}

const MONTHS_NO = ["jan","feb","mar","apr","mai","jun","jul","aug","sep","okt","nov","des"];

const WIND_DIR_NO: Record<string, string> = {
  N: "nord", NNE: "nord-nordøst", NE: "nordøst", ENE: "øst-nordøst",
  E: "øst", ESE: "øst-sørøst", SE: "sørøst", SSE: "sør-sørøst",
  S: "sør", SSW: "sør-sørvest", SW: "sørvest", WSW: "vest-sørvest",
  W: "vest", WNW: "vest-nordvest", NW: "nordvest", NNW: "nord-nordvest",
};

const CARDINALS = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];

function windDirNO(direction: string | null): string | null {
  if (!direction) return null;
  if (WIND_DIR_NO[direction]) return WIND_DIR_NO[direction];
  const deg = parseFloat(direction);
  if (!isNaN(deg)) {
    const card = CARDINALS[Math.round(deg / 22.5) % 16]!;
    return WIND_DIR_NO[card] ?? card;
  }
  return direction;
}

function beaufortNO(ms: number): string {
  if (ms < 0.3) return "stille";
  if (ms < 1.6) return "flau vind";
  if (ms < 3.4) return "svak vind";
  if (ms < 5.5) return "lett bris";
  if (ms < 8.0) return "laber bris";
  if (ms < 10.8) return "frisk bris";
  if (ms < 13.9) return "liten kuling";
  if (ms < 17.2) return "stiv kuling";
  if (ms < 20.8) return "sterk kuling";
  if (ms < 24.5) return "liten storm";
  if (ms < 28.5) return "full storm";
  if (ms < 32.7) return "sterk storm";
  return "orkan";
}

function formatTime(ts: string) {
  const d = new Date(ts);
  return `${d.getDate()}. ${MONTHS_NO[d.getMonth()]} ${d.getFullYear()} kl. ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p: Position = payload[0].payload;
  const dir = windDirNO(p.wind_direction);
  return (
    <div style={{
      background: "rgba(255,255,255,0.95)",
      border: "1px solid #ccc",
      borderRadius: 4,
      padding: "6px 10px",
      fontSize: 12,
      lineHeight: 1.6,
      maxWidth: 200,
    }}>
      {p.placename && <div><strong>{p.placename}</strong></div>}
      {p.altitude != null && <div>{Math.round(p.altitude)} moh.</div>}
      <div style={{ color: "#666" }}>{formatTime(p.timestamp)}</div>
      {p.temperature != null && p.wind_speed != null && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 2 }}>
          {p.weather_symbol && (
            <img
              src={`https://cdn.jsdelivr.net/gh/metno/weathericons/weather/svg/${p.weather_symbol}.svg`}
              width={32} height={32}
              style={{ flexShrink: 0, marginTop: -2 }}
            />
          )}
          <div>
            <div>{p.temperature}°C · {beaufortNO(p.wind_speed)}</div>
            {dir && <div>{p.wind_speed} m/s fra {dir}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ElevationChart({ positions, hoveredIndex, onHover }: Props) {
  if (!positions.length) return null;

  const withAlt = positions.filter((p) => p.altitude != null);
  if (!withAlt.length) return null;

  // Waypoints (non-track messages) get reference lines
  const waypoints = positions.filter((p) => p.type !== "UNLIMITED-TRACK");

  const hoveredDist = hoveredIndex != null ? positions[hoveredIndex]?.distanceM : undefined;

  return (
    <div style={{ width: "100%", height: "100%", background: "#fff" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={withAlt}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          onMouseMove={(e) => {
            if (e.activePayload?.length) {
              const p = e.activePayload[0].payload as Position;
              const idx = positions.findIndex((pos) => pos.id === p.id);
              onHover(idx >= 0 ? idx : null);
            }
          }}
          onMouseLeave={() => onHover(null)}
        >
          <defs>
            <linearGradient id="altGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#145291" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#145291" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis
            dataKey="distanceM"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(v) => `${Math.round(v / 1000)} km`}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={(v) => `${v} m`}
            tick={{ fontSize: 11 }}
            width={52}
          />
          <Tooltip content={<CustomTooltip />} />
          {waypoints.map((p) => (
            <ReferenceLine
              key={p.id}
              x={p.distanceM}
              stroke="#333"
              strokeDasharray="3 3"
              strokeOpacity={0.6}
            />
          ))}
          {hoveredDist != null && (
            <ReferenceLine x={hoveredDist} stroke="orange" strokeWidth={2} />
          )}
          <Area
            type="monotone"
            dataKey="altitude"
            stroke="#145291"
            strokeWidth={2}
            fill="url(#altGradient)"
            dot={false}
            activeDot={{ r: 4, fill: "#145291" }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
